import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { startNewWorkspace } from '../../store/workspaceSlice';
import Cmn_Workspace from './cmn_workspace';
import SaveWorkspaceModal from 'components/authentication/signup-login/SaveWorkspaceModal';
import AuthModalHandler from 'components/authentication/signup-login/AuthModalHandler';
import { useAuth } from 'components/authentication/signup-login/AuthContext';
import { FaRegUserCircle } from "react-icons/fa";
import { MdOutlineKeyboardDoubleArrowRight, MdInfoOutline } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const WorkspaceSidePanel = ({ 
  showWorkspace, 
  setShowWorkspace, 
  onVisualize,
  onSaveSuccess 
}) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();

  // Local states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [modalMode, setModalMode] = useState('save');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingSaveMode, setPendingSaveMode] = useState(null);

  // Redux selectors
  const isWorkspaceLoaded = useSelector(state => state.workspace.isWorkspaceLoaded);
  const loadedWorkspaceName = useSelector(state => state.workspace.loadedWorkspaceName);
  const hasUnsavedChanges = useSelector(state => state.workspace.hasUnsavedChanges);
  const selectedIndicators = useSelector(state => state.workspace.selectedIndicators);
  const selectedMapFeatures = useSelector(state => state.workspace.selectedMapFeatures);

  // Handle save click
  const handleSaveClick = (mode = 'save') => {
    if (!isAuthenticated) {
      setPendingSaveMode(mode);
      setShowAuthModal(true);
      return;
    }

    if (selectedIndicators.length === 0) {
      alert('Please select at least one indicator');
      return;
    }

    setModalMode(mode);
    setShowSaveModal(true);
  };

  // Handle login success
  const handleLoginSuccess = (response) => {
    const loginData = response.data || response;
    let userData = null;
    let token = null;

    if (loginData && loginData.user) {
      userData = loginData.user;
      token = loginData.token;
    } else {
      userData = {
        name: response.name || response.user?.name || 'User',
        email: response.email || response.user?.email || 'user@example.com'
      };
      token = response.token || response.access_token;
    }

    setShowAuthModal(false);

    if (pendingSaveMode) {
      setTimeout(() => {
        // Double-check authentication before opening save modal
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('userData');

        if (token && userData) {
          setModalMode(pendingSaveMode);
          setShowSaveModal(true);
        } else {
          alert('Authentication failed. Please try logging in again.');
        }
        setPendingSaveMode(null);
      }, 1000);
    }
  };

  // Handle new workspace
  const handleNewWorkspace = () => {
    dispatch(startNewWorkspace());
  };

  // Handle internal save success
  const handleInternalSaveSuccess = (workspaceData) => {
    if (onSaveSuccess) {
      onSaveSuccess(workspaceData);
    }
    setShowSaveModal(false);
  };

  if (showWorkspace) {
    return null;
  }

  return (
    <>
    
      {/* Workspace Sidebar */}
      <div
        className="position-fixed end-0 bg-white workspace-scroll shadow"
        style={{
          top: '68px',
          width: '420px',
          height: '100vh',
          zIndex: 1050,
          overflowY: 'scroll',
          padding: '0',
          borderLeft: '1px solid #ddd',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <div className="h-100 border-0">
          <div className="card-header ui-card-header d-flex justify-content-between align-items-center border-0">
            <h5 className="mb-0">
              <FaRegUserCircle /> &nbsp;
              {isWorkspaceLoaded ? loadedWorkspaceName : 'My Workspace'}
              {hasUnsavedChanges && <span className="text-warning ms-2">*</span>}&nbsp;
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="filter-tooltip">
                    <p>Click save to access your work later.</p>
                  </Tooltip>
                }
              >
                <span>
                  <MdInfoOutline style={{ cursor: "pointer", color: "#000000" }} />
                </span>
              </OverlayTrigger>
            </h5>
            <button
              className="btn btn-sm btn-outline-dark"
              onClick={() => setShowWorkspace(true)}
            >
              <MdOutlineKeyboardDoubleArrowRight />
            </button>
          </div>
          <div className="card-body pt-3 p-2">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-end">
                  <div className="p-1">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleSaveClick(isWorkspaceLoaded ? 'update' : 'save')}
                    >
                      <i className={`bi ${isWorkspaceLoaded ? 'bi-arrow-repeat' : 'bi-save'} me-1`}></i>
                      {isWorkspaceLoaded ? 'Update' : 'Save'}
                    </Button>
                  </div>
                  <div className="p-1">
                    <Button 
                      variant="outline-primary" 
                      className="btn-sm f-14" 
                      onClick={() => handleSaveClick('saveAs')}
                    >
                      Save As
                    </Button>
                  </div>
                  <div className="p-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleNewWorkspace}
                    >
                      <i className="bi bi-plus-circle me-1"></i> New
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <Cmn_Workspace 
              visualize={onVisualize}
              showWorkspace={showWorkspace} 
              setShowWorkspace={setShowWorkspace}
            />
          </div>
        </div>
      </div>

      {/* Save Workspace Modal */}
      <SaveWorkspaceModal
        show={showSaveModal}
        handleClose={() => setShowSaveModal(false)}
        onSave={handleInternalSaveSuccess}
        selectedIndicators={selectedIndicators}
        selectedLocations={selectedMapFeatures}
        allMapFeatures={selectedMapFeatures}
        mode={modalMode}
      />

      {/* Auth Modal */}
      <AuthModalHandler
        show={showAuthModal}
        handleClose={() => {
          setShowAuthModal(false);
          setPendingSaveMode(null);
        }}
        initialMode="login"
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default WorkspaceSidePanel;